import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';
import { getCategoryPhotoObject } from '../../../lib/unsplash';
import UnsplashImage from '../../UnsplashImage';
import ClapButton from '../../shared/ClapButton';
import { Card, Badge } from '../../../design-system';

const ActivityCard = memo(function ActivityCard({ activity }) {
  const navigate = useNavigate();
  const [photoObject, setPhotoObject] = useState(activity.unsplashPhoto || null);
  const [loading, setLoading] = useState(!activity.unsplashPhoto);

  useEffect(() => {
    // If activity already has unsplashPhoto, use it
    if (activity.unsplashPhoto) {
      setPhotoObject(activity.unsplashPhoto);
      setLoading(false);
      return;
    }

    // Otherwise, fetch photo object
    if (activity.imageSearchQuery || activity.title) {
      setLoading(true);
      getCategoryPhotoObject('activities', activity, 0).then(photo => {
        setPhotoObject(photo);
        setLoading(false);
      });
    }
  }, [activity]);

  const handleCardClick = (e) => {
    // Don't navigate if clicking on clap button
    if (e.target.closest('button')) {
      return;
    }
    navigate(`/explore/activity/${activity.id}`);
  };

  return (
    <Card
      hover
      onClick={handleCardClick}
      className="group overflow-hidden"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
          </div>
        ) : activity.unsplashPhoto ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={activity.unsplashPhoto}
              alt={activity.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : activity.uploadedImageUrl ? (
          <div className="relative w-full h-full">
            <img
              src={activity.uploadedImageUrl}
              alt={activity.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              onClick={handleCardClick}
            />
            {activity.imageCaption && (
              <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                {activity.imageCaption}
              </div>
            )}
          </div>
        ) : photoObject ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={photoObject}
              alt={activity.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FontAwesomeIcon icon={faPalette} className="text-6xl text-primary/20" />
          </div>
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Badge overlay */}
        <div className="absolute top-3 left-3">
          <Badge glass>
            Activity
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-serif font-bold text-lg text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {activity.title}
        </h3>

        <p className="text-sm text-text-secondary line-clamp-2 mb-4">
          {activity.description}
        </p>

        {/* Meta info */}
        <div className="flex items-center gap-4 text-xs text-text-secondary pb-4 border-b border-border">
          {activity.ageRange && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Ages {activity.ageRange}
            </div>
          )}
          {activity.time && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {activity.time}
            </div>
          )}
        </div>

        {/* Claps and View */}
        <div className="mt-4 flex items-center justify-between">
          {activity.id && (
            <ClapButton 
              contentId={activity.id} 
              contentType="activity" 
              size="sm"
              showCount={true}
            />
          )}
          
          <span className="text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            View activity
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Card>
  );
});

export default ActivityCard;

